import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { reportService } from "../../../../services/api/reportService";
import SearchAndFilterBar from "../../components/SearchAndFilterBar";
import { useNavigate } from "react-router-dom";
import PieChart from "../../components/PieChart";
import Loading from "../../components/Loading";
import { EmptyChart, hasChartData } from "../../components/EmptyChart";
import { getBackendUrl } from "../../../../utils/env";

const toBase64FromPublic = async (path) => {
  try {
    const response = await fetch(path);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image to base64:", error);
    return "";
  }
};

const generateChartImage = async (data, title, totalLabel = "Tổng số", totalFormatter = (v) => v.toLocaleString()) => {
  if (!data || data.length === 0) return "";

  const canvas = document.createElement("canvas");
  canvas.width = 800;
  canvas.height = 400;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const chartCenterX = 200;
  const chartCenterY = 200;
  const radius = 140;

  const normalizedData = data.map((item) => ({
    name: item.name || item.label || item.statusName || "N/A",
    value: parseFloat(item.value || item.count || item.amount || 0),
  }));

  const total = normalizedData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) return "";

  const colors = [
    "#2563EB", "#10B981", "#F97316", "#EF4444", "#8B5CF6",
    "#0EA5E9", "#F59E0B", "#EC4899", "#14B8A6", "#84CC16",
    "#6366F1", "#A855F7",
  ];

  let startAngle = -Math.PI / 2;

  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
  ctx.shadowBlur = 15;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 4;

  normalizedData.forEach((item, index) => {
    const value = item.value;
    const sliceAngle = (value / total) * Math.PI * 2;
    const endAngle = startAngle + sliceAngle;

    const gradient = ctx.createRadialGradient(
      chartCenterX,
      chartCenterY,
      0,
      chartCenterX,
      chartCenterY,
      radius,
    );
    const baseColor = colors[index % colors.length];
    gradient.addColorStop(0, baseColor);
    gradient.addColorStop(1, baseColor + "DD");

    ctx.beginPath();
    ctx.moveTo(chartCenterX, chartCenterY);
    ctx.arc(chartCenterX, chartCenterY, radius, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 3;
    ctx.stroke();

    startAngle = endAngle;
  });

  ctx.restore();

  ctx.beginPath();
  ctx.arc(chartCenterX, chartCenterY, radius * 0.6, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  ctx.textAlign = "center";
  ctx.fillStyle = "#6B7280";
  ctx.font = "400 16px Inter, Arial, sans-serif";
  ctx.fillText(totalLabel, chartCenterX, chartCenterY - 10);

  ctx.fillStyle = "#111827";
  ctx.font = "600 24px Inter, Arial, sans-serif";
  ctx.fillText(totalFormatter(total), chartCenterX, chartCenterY + 25);

  const legendX = 420;
  const lineHeight = 38;
  const totalLegendHeight = normalizedData.length * lineHeight;
  let legendY = chartCenterY - totalLegendHeight / 2 + 10;

  ctx.textAlign = "left";

  normalizedData.forEach((item, index) => {
    const value = item.value;
    const percentage = ((value / total) * 100).toFixed(1);
    const name = item.name;

    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    ctx.fillStyle = colors[index % colors.length];
    ctx.fillRect(legendX, legendY - 10, 16, 16);
    ctx.restore();

    ctx.strokeStyle = "#E5E7EB";
    ctx.lineWidth = 1;
    ctx.strokeRect(legendX, legendY - 10, 16, 16);

    ctx.fillStyle = "#1F2937";
    ctx.font = "600 14px Inter, Arial, sans-serif";
    ctx.fillText(name, legendX + 24, legendY);

    ctx.font = "400 12px Inter, Arial, sans-serif";
    ctx.fillStyle = "#6B7280";
    ctx.fillText(
      `${totalFormatter(value)} (${percentage}%)`,
      legendX + 24,
      legendY + 16,
    );

    legendY += lineHeight;
  });

  ctx.fillStyle = "#111827";
  ctx.font = "600 16px Inter, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(title, canvas.width / 2, 25);

  return canvas.toDataURL("image/png");
};

const buildPrintHTML = (bodyHTML, title = "Báo cáo") => {
  return `<!doctype html>
<html lang="vi">
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    :root{--page-w:1123px;--page-h:794px;}
    *{box-sizing:border-box;margin:0;padding:0}
    html,body{background:#fff;color:#111;font-family:Arial,"Times New Roman",serif}
    @page{size:1123px 794px;margin:36px 40px 64px 40px}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    .pdf-root{width:100%;line-height:1.35;font-size:13px;padding-bottom:52px}
    .wm-overlay-fixed{
      position:fixed;
      width:100%;
      height:100%;
      top:0;left:0;
      background-size:contain;
      background-repeat:no-repeat;
      background-position:center;
      opacity:0.12;
      pointer-events:none;
      z-index:999999;
    }
    .heading{
      text-align:center;
      font-size:20px;
      font-weight:600;
      color:#0457C2;
      padding-bottom:10px;
      margin-bottom:10px;
      page-break-after:avoid;
    }
    .section-title{
      font-size:16px;
      font-weight:600;
      color:#0457C2;
      margin-top:18px;
      margin-bottom:12px;
      padding-bottom:8px;
      border-bottom:1px solid #E5E7EB;
      page-break-after:avoid;
    }
    .chart-container{
      text-align:center;
      margin:16px 0;
      page-break-inside:avoid;
    }
    .chart-container img{
      max-width:100%;
      max-height:350px;
      height:auto;
      border-radius:8px;
      padding:10px;
      background:#fff;
      object-fit:contain;
    }
    .report-section{
      page-break-inside:avoid;
      display:block;
      width:100%;
      margin-bottom: 20px;
    }
    .report-footer{
      position:fixed;
      left:40px;
      right:40px;
      bottom:18px;
      text-align:center;
      color:#374151;
      font-size:10.5px;
      line-height:1.35;
    }
  </style>
</head>
<body>
  <div class="wm-overlay-fixed"></div>
  <div class="report-footer">
    <p>Báo cáo được tổng hợp vào ngày ${new Date().toLocaleDateString("vi-VN")} từ Nền tảng số Sohuutritue.vn</p>
    <p>Tổng đài tư vấn Sở hữu trí tuệ: 08 1900 9697 (điện thoại, zalo) - Email: luatsu@sohuutritue.vn</p>
  </div>
  <div class="pdf-root">${bodyHTML}</div>
</body>
</html>`;
};

const CommonReport = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [serviceReport, setServiceReport] = useState(null);
  const [profileReport, setProfileReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const [hoveredCard, setHoveredCard] = useState(null);

  const convertToAPIFormat = (dateStr) => {
    if (!dateStr) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        overview: true,
        fromDate: fromDate ? convertToAPIFormat(fromDate) : undefined,
        toDate: toDate ? convertToAPIFormat(toDate) : undefined,
      };

      const [serviceRes, profileRes, financialRes] = await Promise.all([
        reportService.get_report_service(token, params),
        reportService.get_report_profile(token, params),
        reportService.get_report_finance(token, params),
      ]);

      setServiceReport(serviceRes);
      setProfileReport(profileRes);
      setFinancialReport(financialRes);
    } catch (err) {
      console.error("Lỗi lấy báo cáo:", err);
      toast.error("Không thể tải báo cáo");
    } finally {
      setLoading(false);
    }
  }, [token, fromDate, toDate]);

  const handleApplyFilter = () => {
    if (toDate && !fromDate) {
      return toast.error("Vui lòng nhập ngày bắt đầu.");
    }
    if (fromDate && !toDate) {
      return toast.error("Vui lòng nhập ngày kết thúc.");
    }

    if (fromDate && toDate) {
      const from = new Date(fromDate.split("-").reverse().join("-"));
      const to = new Date(toDate.split("-").reverse().join("-"));
      if (from > to) {
        return toast.error("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
      }
    }

    fetchReports();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + " VNĐ";
  };

  const handleExport = async () => {
    try {
      if (toDate && !fromDate) {
        return toast.error("Vui lòng nhập ngày bắt đầu.");
      }
      if (fromDate && !toDate) {
        return toast.error("Vui lòng nhập ngày kết thúc.");
      }

      if (fromDate && toDate) {
        const from = new Date(fromDate.split("-").reverse().join("-"));
        const to = new Date(toDate.split("-").reverse().join("-"));
        if (from > to) {
          return toast.error("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
        }
      }

      setExporting(true);

      const params = {};

      if (fromDate) {
        params.fromDate = convertToAPIFormat(fromDate);
      }
      if (toDate) {
        params.toDate = convertToAPIFormat(toDate);
      }

      const [serviceExport, profileExport, financialExport] = await Promise.all([
        reportService.get_report_service(token, { ...params, overview: true }),
        reportService.get_report_profile(token, { ...params, overview: true }),
        reportService.get_report_finance(token, { ...params, overview: true }),
      ]);

      // Generate chart images for all three reports
      const serviceChartImg = hasChartData(serviceExport?.chart)
        ? await generateChartImage(serviceExport.chart, "Biểu đồ dịch vụ")
        : "";
      const profileChartImg = hasChartData(profileExport?.chart)
        ? await generateChartImage(profileExport.chart, "Biểu đồ hồ sơ")
        : "";
      const financialChartImg = hasChartData(financialExport?.chart)
        ? await generateChartImage(
          financialExport.chart,
          "Biểu đồ tài chính",
          "Tổng tiền",
          (v) => new Intl.NumberFormat("vi-VN").format(v) + " ₫"
        )
        : "";

      let bodyHTML = `<h1 class="heading">BÁO CÁO TỔNG QUAN</h1>`;

      if (fromDate || toDate) {
        bodyHTML += `
          <div style="font-size:12px;color:#374151;margin-bottom:20px;text-align:center">
            <b>Thời gian:</b> ${fromDate || "..."} → ${toDate || "..."}
          </div>
        `;
      }

      // 1. Service Report Section
      bodyHTML += `
        <div class="report-section">
          <h2 class="section-title">1. Báo cáo dịch vụ</h2>
          ${serviceChartImg
          ? `<div class="chart-container"><img src="${serviceChartImg}" alt="Biểu đồ dịch vụ" /></div>`
          : `<p style="text-align:center;color:#6b7280;padding:20px">Không có dữ liệu dịch vụ</p>`
        }
        </div>
      `;

      // 2. Profile Report Section
      bodyHTML += `
        <div class="report-section">
          <h2 class="section-title">2. Báo cáo hồ sơ</h2>
          ${profileChartImg
          ? `<div class="chart-container"><img src="${profileChartImg}" alt="Biểu đồ hồ sơ" /></div>`
          : `<p style="text-align:center;color:#6b7280;padding:20px">Không có dữ liệu hồ sơ</p>`
        }
        </div>
      `;

      // 3. Financial Report Section
      bodyHTML += `
        <div class="report-section">
          <h2 class="section-title">3. Báo cáo tài chính</h2>
          <div style="margin-bottom:10px; display: flex; gap: 20px; justify-content: center;">
            <div style="background:#f5f5f5; border:1px solid #d2d5da; border-radius:8px; padding:10px 14px; font-size:13px;">
              <b>Đã thanh toán:</b> ${formatCurrency(financialExport?.summary?.paidAmount || 0)}
            </div>
            <div style="background:#f5f5f5; border:1px solid #d2d5da; border-radius:8px; padding:10px 14px; font-size:13px;">
              <b>Chưa thanh toán:</b> ${formatCurrency(financialExport?.summary?.unpaidAmount || 0)}
            </div>
          </div>
          ${financialChartImg
          ? `<div class="chart-container"><img src="${financialChartImg}" alt="Biểu đồ tài chính" /></div>`
          : `<p style="text-align:center;color:#6b7280;padding:20px">Không có dữ liệu tài chính</p>`
        }
        </div>
      `;

      const html = buildPrintHTML(bodyHTML, "Báo cáo tổng quan");
      const watermarkBase64 = await toBase64FromPublic("/images/watermark-45.png");
      const BE_URL = getBackendUrl();

      const response = await fetch(`${BE_URL}/pdf/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          html,
          profileName: "Báo cáo tổng quan",
          watermarkBase64,
          watermarkOpacity: 0.12,
          options: { width: 1123, height: 794, deviceScaleFactor: 2 },
          watermarkMode: "fixed",
        }),
      });

      if (!response.ok) throw new Error("Không thể tạo PDF");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bao-cao-tong-quan-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Xuất báo cáo thành công!");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Xuất PDF thất bại");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || exporting) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full bg-gray-50 gap-6">
      {/* Header */}
      <div className="shrink-0 flex flex-row items-center justify-between w-full">
        <h1 className="text-2xl whitespace-nowrap font-semibold text-gray-800">
          Báo cáo tổng quan
        </h1>

        <div className="w-full flex flex-row items-center justify-end gap-3">
          <SearchAndFilterBar
            showSearchInput={false}
            onSearchClick={handleApplyFilter}
            showDatePickers={true}
            fromDatePlaceholder="Từ ngày"
            fromDateValue={fromDate}
            toDatePlaceholder="Đến ngày"
            toDateValue={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            showFilter={true}
            filterLabel="Áp dụng"
            showAdditionBtn={false}
          />

          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-blue-600 text-white px-4 py-[9px] rounded-lg hover:bg-blue-700 disabled:bg-gray-400 text-sm flex items-center gap-2 h-[42px] transition-all"
          >
            {exporting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Đang xuất...
              </>
            ) : (
              <>
                Xuất file
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.66667 6.66667L8 10L11.3333 6.66667"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 10V2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <div className="bg-white rounded-[16px] border border-[#D2D5DA] p-4 w-full h-full min-h-0 overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
            {/* ===== Card: Service ===== */}
            <div
              onClick={() => navigate("/bao-cao-dich-vu")}
              onMouseEnter={() => setHoveredCard("service")}
              onMouseLeave={() => setHoveredCard(null)}
              className="w-full rounded-xl p-4 flex flex-col gap-6 cursor-pointer transition-all duration-300"
              style={{
                background:
                  hoveredCard === "service"
                    ? "linear-gradient(225deg, #C8FEE2 -10.52%, #F6E6FA 100.34%)"
                    : "#EFEFEF",
              }}
            >
              <div>
                <h2 className="text-lg font-semibold">Báo cáo dịch vụ</h2>
              </div>

              <div
                className="rounded-[16px] p-4 transition-all duration-300 overflow-visible"
                style={{
                  background:
                    hoveredCard === "service"
                      ? "rgba(255, 255, 255, 0.50)"
                      : "#F9F9F9",
                  backdropFilter: hoveredCard === "service" ? "blur(2px)" : "none",
                }}
              >
                {hasChartData(serviceReport?.chart) ? (
                  <PieChart
                    data={serviceReport?.chart || []}
                    size={280}
                    legendPosition="bottom"
                  />
                ) : (
                  <EmptyChart title="Chưa có dữ liệu dịch vụ" />
                )}
              </div>

              <div className="flex justify-end items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/bao-cao-dich-vu");
                  }}
                  className="bg-black text-white px-3 py-[7px] rounded-[16px] hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  Xem chi tiết <span>→</span>
                </button>
              </div>
            </div>

            <div
              onClick={() => navigate("/bao-cao-ho-so")}
              onMouseEnter={() => setHoveredCard("profile")}
              onMouseLeave={() => setHoveredCard(null)}
              className="w-full rounded-xl p-4 flex flex-col gap-6 cursor-pointer transition-all duration-300"
              style={{
                background:
                  hoveredCard === "profile"
                    ? "linear-gradient(225deg, #C8FEE2 -10.52%, #F6E6FA 100.34%)"
                    : "#EFEFEF",
              }}
            >
              <div>
                <h2 className="text-lg font-semibold">Báo cáo hồ sơ</h2>
              </div>

              <div
                className="rounded-[16px] p-4 transition-all duration-300 overflow-visible"
                style={{
                  background:
                    hoveredCard === "profile"
                      ? "rgba(255, 255, 255, 0.50)"
                      : "#F9F9F9",
                  backdropFilter: hoveredCard === "profile" ? "blur(2px)" : "none",
                }}
              >
                {hasChartData(profileReport?.chart) ? (
                  <PieChart
                    data={profileReport?.chart || []}
                    size={280}
                    legendPosition="bottom"
                  />
                ) : (
                  <EmptyChart title="Chưa có dữ liệu hồ sơ" />
                )}
              </div>

              <div className="flex justify-end items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/bao-cao-ho-so");
                  }}
                  className="bg-black text-white px-3 py-[7px] rounded-[16px] hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  Xem chi tiết <span>→</span>
                </button>
              </div>
            </div>

            {/* ===== Card: Financial ===== */}
            <div
              onClick={() => navigate("/bao-cao-tai-chinh")}
              onMouseEnter={() => setHoveredCard("financial")}
              onMouseLeave={() => setHoveredCard(null)}
              className="w-full rounded-xl p-4 flex flex-col gap-6 cursor-pointer transition-all duration-300"
              style={{
                background:
                  hoveredCard === "financial"
                    ? "linear-gradient(225deg, #C8FEE2 -10.52%, #F6E6FA 100.34%)"
                    : "#EFEFEF",
              }}
            >
              <div>
                <h2 className="text-lg font-semibold">Báo cáo tài chính</h2>
              </div>

              <div
                className="rounded-[16px] p-4 transition-all duration-300 overflow-visible"
                style={{
                  background:
                    hoveredCard === "financial"
                      ? "rgba(255, 255, 255, 0.50)"
                      : "#F9F9F9",
                  backdropFilter:
                    hoveredCard === "financial" ? "blur(2px)" : "none",
                }}
              >
                {hasChartData(financialReport?.chart) ? (
                  <PieChart
                    data={financialReport?.chart || []}
                    size={280}
                    legendPosition="bottom"
                  />
                ) : (
                  <EmptyChart title="Chưa có dữ liệu tài chính" />
                )}
              </div>

              <div className="flex justify-end items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/bao-cao-tai-chinh");
                  }}
                  className="bg-black text-white px-3 py-[7px] rounded-[16px] hover:bg-gray-800 flex items-center justify-center gap-2"
                >
                  Xem chi tiết <span>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonReport;
